import { Fragment, useEffect, useState } from 'react';
import { api, money } from '../api.js';
import { ReportHead, Money, Empty } from '../components/Shell.jsx';
import { addQuantity, formatQuantity, isZeroQuantity } from '../lib/quantity.js';

/**
 * The item master: labour, laptops and cans of coke in ONE list.
 *
 * KIND IS A COLUMN AND A FILTER, NEVER A SET OF TABS. Tabs would ask the user
 * which kind a thing is before they are allowed to look for it, and half the
 * time the answer to "is this stocked?" is the very thing they came here to
 * find out. One list, sorted by code, with kind as a column you can narrow on
 * — the same way the chart of accounts does not put assets behind a tab.
 *
 * QUANTITY FOR A non_inventory ROW IS `—`, ABSENT, NOT ZERO. A labour code is
 * not out of stock; it is not stocked at all. A zero says "we counted and there
 * were none", which is a true statement about a can of coke with an empty shelf
 * and a false one about an hour of somebody's time. The API is careful to send
 * null rather than 0 for exactly this reason (projectItem rule 1, which is the
 * pageCount reasoning from slips.js applied to quantity), and formatQuantity is
 * careful to render null as a dash. Neither care is worth anything if this
 * screen puts `?? 0` in front of it.
 */
const KIND_LABEL = {
  non_inventory: 'Not stocked',
  quantified: 'Quantified',
  individual: 'Individual',
};

const KIND_HELP = {
  non_inventory: 'Labour, delivery, a fee. It is bought or sold but never sits on a shelf, so it '
    + 'has no quantity, no locations and no cost layers.',
  quantified: 'Measured in units: cans, kilograms, metres. One line on a count sheet, one figure.',
  individual: 'Each one distinguishable by a serial number — a laptop, a machine. One line on a '
    + 'count sheet per unit, answered found or not found.',
};

const MOVEMENT_LABEL = {
  opening: 'Opening', purchase: 'Purchase', sale: 'Sale', adjustment: 'Adjustment',
  count: 'Count', transfer: 'Transfer', reversal: 'Reversal', return: 'Return',
};

/**
 * How many items are asked for at a time.
 *
 * The API defaults to 200 and this asks for the same number ON PURPOSE, so
 * that a full page is a signal rather than a coincidence: when the list comes
 * back exactly this long there may be more behind it, and the screen says so
 * instead of quietly pretending the item master ends here.
 */
const LIMIT = 200;

/** An account list, narrowed to what can actually be posted to. */
const postable = (accounts, subtypes) => accounts.filter(
  (a) => a.is_postable && a.is_active && subtypes.includes(a.subtype));

export function Items({ entity }) {
  const caps = entity?.capabilities ?? [];
  const canSeeStock = caps.includes('stock.view');

  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState({ kind: '', q: '', active: 'true' });
  const [accounts, setAccounts] = useState([]);
  const [taxCodes, setTaxCodes] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState({});       // itemId -> { loading, error, ledger }
  const [editing, setEditing] = useState(null);   // item id, or 'new'
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);

  const load = () => {
    const p = new URLSearchParams();
    if (filter.kind) p.set('kind', filter.kind);
    if (filter.q.trim()) p.set('q', filter.q.trim());
    if (filter.active) p.set('active', filter.active);
    // THE LIMIT IS ASKED FOR, NOT INHERITED. The route defaults to 200 whether
    // or not anyone knows it does, and a company past that number would be
    // shown a silently short list — the worst kind of wrong, because nothing on
    // the screen says anything is missing. Asking for it explicitly is what
    // makes `items.length === LIMIT` mean "there may be more" below.
    p.set('limit', String(LIMIT));
    return api.get(`/api/entities/${entity.id}/stock-items?${p}`)
      .then((d) => setItems(d.items))
      .catch((e) => { setErr(e.message); setItems([]); });
  };

  useEffect(() => { if (entity?.id) load(); /* eslint-disable-next-line */ }, [entity?.id, filter]);

  useEffect(() => {
    if (!entity?.id) return;
    api.get(`/api/entities/${entity.id}/accounts`).then((d) => setAccounts(d.accounts)).catch(() => {});
    api.get(`/api/entities/${entity.id}/tax-codes`).then((d) => setTaxCodes(d.taxCodes)).catch(() => {});
  }, [entity?.id]);

  /**
   * What one item's stock looks like, fetched only when a row is opened.
   *
   * The item ledger is the one read that answers both halves of the expanded
   * row — serials for an individual item, per-location quantity for a
   * quantified one — and it answers a third question the edit form needs: has
   * this item MOVED? A row in the ledger is a movement, and a moved item has
   * its kind and its code frozen by 032.
   *
   * `from` is deliberately far back rather than the financial year: the report
   * runs its totals from inception whatever `from` says, and a narrower window
   * would hide the movements that do the freezing.
   */
  const toggleRow = (item) => {
    if (expanded === item.id) { setExpanded(null); setEditing(null); return; }
    openRow(item);
  };

  // Opens without toggling. Edit calls THIS: routing it through the toggle
  // closed an already-open row, and the form renders inside that row, so
  // pressing Edit on an open item made the form it was asking for disappear.
  const openRow = async (item) => {
    setExpanded(item.id);
    if (item.kind === 'non_inventory' || !canSeeStock || detail[item.id]?.ledger) return;

    setDetail((d) => ({ ...d, [item.id]: { loading: true } }));
    const to = new Date().toISOString().slice(0, 10);
    try {
      const data = await api.get(`/api/reports/${entity.id}/stock-item-ledger`
        + `?itemId=${item.id}&from=1900-01-01&to=${to}`);
      setDetail((d) => ({ ...d, [item.id]: { ledger: data } }));
    } catch (e) {
      setDetail((d) => ({ ...d, [item.id]: { error: e.message } }));
    }
  };

  const saved = (what) => { setEditing(null); setMsg(what); setDetail({}); load(); };

  return (
    <>
      <ReportHead entity={entity} title="Items"
                  sub="Everything this company buys or sells — stocked or not, in one list." />
      {err && <div className="error">{err}</div>}
      {msg && <div className="ok">{msg}</div>}

      <div className="controls">
        <label className="field" style={{ minWidth: 220 }}>
          <span>Search</span>
          <input value={filter.q} placeholder="Code, name or barcode"
                 onChange={(e) => setFilter({ ...filter, q: e.target.value })} />
        </label>
        <label className="field">
          <span>Kind</span>
          <select value={filter.kind} onChange={(e) => setFilter({ ...filter, kind: e.target.value })}>
            <option value="">Every kind</option>
            {Object.entries(KIND_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </label>
        <label className="field">
          <span>Showing</span>
          <select value={filter.active} onChange={(e) => setFilter({ ...filter, active: e.target.value })}>
            <option value="true">Active only</option>
            <option value="false">Inactive only</option>
            <option value="">Both</option>
          </select>
        </label>
        <button className="btn" onClick={() => { setEditing('new'); setExpanded(null); }}>
          Add an item
        </button>
      </div>

      {editing === 'new' && (
        <ItemForm entity={entity} accounts={accounts} taxCodes={taxCodes}
                  onCancel={() => setEditing(null)}
                  onSaved={(code) => saved(`Item ${code} added.`)} />
      )}

      {items === null ? (
        <div className="empty">Loading…</div>
      ) : items.length === 0 ? (
        <Empty>
          <p>No items match that.</p>
          <p>Add one, or widen the filter.</p>
        </Empty>
      ) : (
        <>
        {items.length >= LIMIT && (
          <div className="note note--warn">
            <strong>This list stops at {LIMIT} items.</strong> There are more, and they are not
            below. Narrow it with the search box or the kind filter — a list that quietly ends
            teaches you an item does not exist when it simply was not asked for.
          </div>
        )}
        <div className="scroll-x">
          <table className="ledger">
            <thead>
              <tr>
                <th style={{ width: 110 }}>Code</th>
                <th>Item</th>
                <th style={{ width: 110 }}>Kind</th>
                <th style={{ width: 70 }}>Unit</th>
                <th className="num" style={{ width: 110 }}>On hand</th>
                <th className="num" style={{ width: 120 }}>Sells for</th>
                <th style={{ width: 150 }} />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <Fragment key={item.id}>
                  <tr style={{ opacity: item.isActive ? 1 : 0.5 }}>
                    <td className="code figure">{item.code}</td>
                    <td>
                      {item.name}
                      {!item.isActive && <span className="neg"> · inactive</span>}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--ink-soft)' }}>{KIND_LABEL[item.kind]}</td>
                    <td style={{ fontSize: 12, color: 'var(--ink-soft)' }}>{item.unit ?? '—'}</td>
                    {/* formatQuantity(null) is '—'. NOT `?? 0` — see the head of
                        this file: a dash and a zero are different facts. */}
                    <td className="num figure">{formatQuantity(item.onHandQty)}</td>
                    <td className="num">
                      {item.defaultSellPrice !== null
                        ? <Money value={item.defaultSellPrice} />
                        : item.markupPct !== null
                          ? <span style={{ fontSize: 12, color: 'var(--ink-soft)' }}>
                              {item.markupPct}% markup
                            </span>
                          : '—'}
                    </td>
                    <td className="num">
                      <button className="icon-btn" onClick={() => toggleRow(item)}>
                        {expanded === item.id ? 'Close' : 'Details'}
                      </button>{' '}
                      <button className="icon-btn"
                              onClick={() => { setEditing(item.id); openRow(item); }}>
                        Edit
                      </button>
                    </td>
                  </tr>

                  {expanded === item.id && (
                    <tr>
                      <td colSpan={7} style={{ background: 'var(--paper-sunk)' }}>
                        <ItemDetail item={item} detail={detail[item.id]} canSeeStock={canSeeStock}
                                    accounts={accounts} />
                        {editing === item.id && (
                          <ItemForm entity={entity} item={item} accounts={accounts} taxCodes={taxCodes}
                                    hasMoved={hasMoved(item, detail[item.id])}
                                    movedLoading={movedLoading(item, detail[item.id], canSeeStock)}
                                    movedUnknown={movedUnknown(item, detail[item.id], canSeeStock)}
                                    onCancel={() => setEditing(null)}
                                    onSaved={(code) => saved(`Item ${code} saved.`)} />
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}
    </>
  );
}

/**
 * Has this item moved, so that 032 has frozen its kind and its code?
 *
 * A ledger row IS a movement, so any row at all is the answer. A non_inventory
 * item can have no layers and no movements by definition, so it is never
 * frozen — which is exactly why it is safe that the ledger report excludes it.
 */
const hasMoved = (item, detail) =>
  item.kind !== 'non_inventory' && (detail?.ledger?.rows?.length ?? 0) > 0;

/**
 * Is the answer still on its way?
 *
 * A THIRD STATE, and it has to be one. While the ledger is loading, hasMoved is
 * false and movedUnknown is true — so without this the form would open with
 * kind and code ENABLED and a note saying the check could not be made, then
 * flip to frozen a moment later. A safety control that is momentarily wrong is
 * worse than one that is slow: the user reads the first state, believes it, and
 * the correction arrives after they have already decided what they can do.
 */
const movedLoading = (item, detail, canSeeStock) =>
  item.kind !== 'non_inventory' && canSeeStock && Boolean(detail?.loading);

/**
 * True when we CANNOT KNOW whether the item has moved — the reader holds
 * stock.items but not stock.view, or the ledger call failed.
 *
 * The form then leaves the two frozen fields enabled and says the API is the
 * backstop, rather than disabling a correction somebody is entitled to make.
 * Disabling on a guess would make a legitimate edit impossible for everyone in
 * order to pre-empt a refusal for some — the same trap the PATCH schema
 * deliberately avoids by keeping `kind` in it.
 */
const movedUnknown = (item, detail, canSeeStock) =>
  item.kind !== 'non_inventory'
  && !movedLoading(item, detail, canSeeStock)
  && (!canSeeStock || Boolean(detail?.error) || !detail?.ledger);

/** The expanded row, which switches three ways on kind. */
function ItemDetail({ item, detail, canSeeStock, accounts }) {
  const accountName = (id) => {
    const a = accounts.find((x) => x.id === id);
    return a ? `${a.code} ${a.name}` : null;
  };

  return (
    <div style={{ padding: '4px 2px 12px' }}>
      <div className="note" style={{ marginTop: 0 }}>{KIND_HELP[item.kind]}</div>

      <div className="stat-row">
        <div className="stat">
          <div className="stat__label">Revenue</div>
          <div className="stat__value" style={{ fontSize: 14 }}>{accountName(item.revenueAccountId) ?? '—'}</div>
        </div>
        <div className="stat">
          <div className="stat__label">{item.kind === 'non_inventory' ? 'Expense' : 'Cost of sales'}</div>
          <div className="stat__value" style={{ fontSize: 14 }}>
            {accountName(item.kind === 'non_inventory' ? item.expenseAccountId : item.cosAccountId) ?? '—'}
          </div>
        </div>
        <div className="stat">
          <div className="stat__label">Sells for</div>
          <div className="stat__value" style={{ fontSize: 14 }}>
            {item.defaultSellPrice !== null ? money(item.defaultSellPrice)
              : item.markupPct !== null ? `${item.markupPct}% markup` : '—'}
          </div>
        </div>
        <div className="stat">
          <div className="stat__label">Barcode</div>
          <div className="stat__value" style={{ fontSize: 14 }}>{item.barcode ?? '—'}</div>
        </div>
      </div>

      {item.description && (
        <div style={{ margin: '10px 0', fontSize: 13, color: 'var(--ink-soft)' }}>{item.description}</div>
      )}

      {/* --- and here the row switches three ways ------------------------- */}
      {item.kind === 'non_inventory' ? (
        // NOTHING STOCK-SHAPED. Not an empty quantity table, not a zero — this
        // item has no quantity, no locations and no cost layers, and saying so
        // in words is the only honest rendering.
        <div className="note">
          Not a stocked item. There is no quantity, no location and no cost layer behind it — it
          never appears on a count sheet and never carries a value in the stock valuation.
        </div>
      ) : !canSeeStock ? (
        // ⚠ LATENT, NOT LIVE — and left in deliberately rather than deleted.
        //
        // This screen is reached with `stock.items` (the nav mapping the brief
        // fixed) while the item list and the stock reports are gated on
        // `stock.view`. Those are different capabilities, so a role holding the
        // first without the second would see this screen and be refused its
        // figures. NO ROLE IN capabilities.js HOLDS THAT COMBINATION: every
        // grant of stock.items comes with stock.view, so this branch cannot
        // render today and is not covered by anything.
        //
        // It stays because the mismatch is real and a future role could open
        // it, and because a reader who found the branch missing would conclude
        // the two capabilities are the same thing. `stock-locations` has the
        // identical mismatch — nav on stock.items, API list on stock.view —
        // and no equivalent branch, because that screen has nothing to degrade
        // to: it would simply fail to load its list.
        <div className="note">
          Quantities are not shown for your role.
        </div>
      ) : detail?.loading ? (
        <div className="empty">Loading…</div>
      ) : detail?.error ? (
        <div className="error">{detail.error}</div>
      ) : item.kind === 'individual' ? (
        <Serials ledger={detail?.ledger} />
      ) : (
        <ByLocation ledger={detail?.ledger} unit={item.unit} />
      )}
    </div>
  );
}

/**
 * A layer's specifications, as they were typed.
 *
 * ABSENT READS `—`, NOT AN EMPTY LIST. A layer with no specs was never
 * described; that is a different fact from "described as nothing", and it is
 * the same distinction quantity draws between a dash and a zero.
 *
 * Deliberately renders whatever keys are there rather than a fixed set of
 * fields: specs are jsonb precisely because a laptop, a lathe and a delivery
 * van are not described by the same words, and a schema here would silently
 * hide every key it did not expect.
 */
function Specs({ specs }) {
  const entries = specs && typeof specs === 'object' ? Object.entries(specs) : [];
  if (entries.length === 0) return <span style={{ color: 'var(--ink-faint)' }}>—</span>;
  return (
    <span style={{ fontSize: 12, color: 'var(--ink-soft)' }}>
      {entries.map(([k, v], i) => (
        <Fragment key={k}>
          {i > 0 ? ' · ' : ''}
          <span style={{ color: 'var(--ink-faint)' }}>{k}</span>{' '}
          {typeof v === 'object' ? JSON.stringify(v) : String(v)}
        </Fragment>
      ))}
    </span>
  );
}

/** An individual item: which serials are on hand, and how each one arrived. */
function Serials({ ledger }) {
  const rows = ledger?.rows ?? [];
  const bySerial = new Map();
  for (const r of rows) {
    const key = r.serial_no ?? '—';
    const held = bySerial.get(key) ?? { serial: key, qty: '0.0000', last: null, specs: null };
    held.qty = addQuantity(held.qty, r.quantity);
    held.last = r;
    // The layer's specs, carried by 045 — THE LATEST, alongside held.last,
    // and not the first.
    //
    // "One serial is one layer" is not what the schema promises. 029's Rule 3
    // makes a serial unique only among units ON HAND, and 029_stock.sql:612
    // says why a plain unique index was rejected: it would refuse a real
    // transaction, a machine sold and later bought back. That is a second layer
    // with the same serial and possibly a different description — a repaired
    // machine with more memory in it — and taking the first would print the
    // older one. Equally safe against a reversal mirror, which shares its
    // original's layer and therefore its specs.
    held.specs = r.specs ?? null;
    bySerial.set(key, held);
  }
  const onHand = [...bySerial.values()].filter((s) => !isZeroQuantity(s.qty));

  if (onHand.length === 0) {
    return <div className="note">None of this item's units is on hand today.</div>;
  }

  return (
    <div className="scroll-x">
      <table className="ledger">
        <thead>
          <tr>
            <th style={{ width: 180 }}>Serial</th>
            {/* Beside the serial, because together they are what makes an
                individual item individual — one machine, not a model. */}
            <th>Specifications</th>
            <th style={{ width: 180 }}>Where</th>
            <th style={{ width: 110 }}>Since</th>
            <th style={{ width: 160 }}>Last movement</th>
          </tr>
        </thead>
        <tbody>
          {onHand.map((s) => (
            <tr key={s.serial}>
              <td className="figure">{s.serial}</td>
              <td><Specs specs={s.specs} /></td>
              <td>{s.last?.location_code} {s.last?.location_name}</td>
              <td className="figure">{s.last?.movement_date}</td>
              <td style={{ fontSize: 12, color: 'var(--ink-soft)' }}>
                {MOVEMENT_LABEL[s.last?.kind] ?? s.last?.kind}
                {s.last?.reference ? ` · ${s.last.reference}` : ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** A quantified item: how much is where, and what the lot is worth. */
function ByLocation({ ledger, unit }) {
  const rows = ledger?.rows ?? [];
  const byLocation = new Map();
  for (const r of rows) {
    const held = byLocation.get(r.location_id)
      ?? { code: r.location_code, name: r.location_name, qty: '0.0000' };
    held.qty = addQuantity(held.qty, r.quantity);
    byLocation.set(r.location_id, held);
  }
  const places = [...byLocation.values()].filter((l) => !isZeroQuantity(l.qty));

  if (places.length === 0) {
    return <div className="note">Nothing of this item is on hand today.</div>;
  }

  return (
    <div className="scroll-x">
      <table className="ledger">
        <thead>
          <tr>
            <th>Location</th>
            <th className="num" style={{ width: 140 }}>Quantity</th>
          </tr>
        </thead>
        <tbody>
          {places.map((l) => (
            <tr key={l.code}>
              <td><span className="code figure">{l.code}</span> {l.name}</td>
              <td className="num figure">{formatQuantity(l.qty)} {unit ?? ''}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td>
              Value across every location
              {/* Cost lives on the layer and a layer has no location, so this
                  is the item's whole value and cannot be split by shelf. */}
            </td>
            <td className="num"><Money value={ledger?.closing?.value ?? '0.00'} /></td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

/**
 * Create or edit an item. The fields shown follow the kind, because the rules
 * do:
 *
 *  - a non_inventory item MAY NOT have a cost of sales account — it is never
 *    issued from stock, so there is no cost to land anywhere. It gets an
 *    expense account instead.
 *  - a stocked item MUST have one, or issuing it produces a cost figure with
 *    nothing behind it.
 *  - an item has a selling price OR a markup, never both. Two answers to
 *    "what does this sell for" means the invoice uses whichever the code
 *    happened to check first.
 *
 * All three are enforced by the database and re-stated by stock.js. Showing
 * them here is not a second implementation of the rules — it is so the user
 * meets the shape of the item before they meet a refusal.
 */
function ItemForm({
  entity, item, accounts, taxCodes, hasMoved, movedLoading, movedUnknown, onCancel, onSaved,
}) {
  const editing = Boolean(item);
  const [form, setForm] = useState(() => ({
    kind: item?.kind ?? 'quantified',
    code: item?.code ?? '',
    name: item?.name ?? '',
    description: item?.description ?? '',
    unit: item?.unit ?? 'each',
    barcode: item?.barcode ?? '',
    revenueAccountId: item?.revenueAccountId ?? '',
    cosAccountId: item?.cosAccountId ?? '',
    expenseAccountId: item?.expenseAccountId ?? '',
    salesTaxCodeId: item?.salesTaxCodeId ?? '',
    purchaseTaxCodeId: item?.purchaseTaxCodeId ?? '',
    defaultSellPrice: item?.defaultSellPrice ?? '',
    markupPct: item?.markupPct ?? '',
    isActive: item?.isActive ?? true,
  }));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const stocked = form.kind !== 'non_inventory';
  // FROZEN WHILE THE ANSWER IS STILL COMING. The safe state is the one that
  // does not have to be taken back.
  const frozen = editing && (hasMoved || movedLoading);

  const revenue = postable(accounts, ['revenue', 'other_income']);
  const cos = postable(accounts, ['cost_of_sales']);
  const expense = postable(accounts, ['operating_expense', 'cost_of_sales', 'finance_cost']);
  const output = taxCodes.filter((t) => t.direction === 'output');
  const input = taxCodes.filter((t) => t.direction === 'input');

  const save = async () => {
    setBusy(true); setErr(null);
    const body = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      unit: form.unit.trim() || 'each',
      barcode: form.barcode.trim() || null,
      revenueAccountId: form.revenueAccountId || null,
      salesTaxCodeId: form.salesTaxCodeId || null,
      purchaseTaxCodeId: form.purchaseTaxCodeId || null,
      // Price and markup are exclusive, so whichever is blank is sent as null
      // rather than omitted — an edit that clears a price has to say so.
      defaultSellPrice: String(form.defaultSellPrice).trim() || null,
      markupPct: String(form.markupPct).trim() || null,
      isActive: form.isActive,
    };
    // ONLY THE ACCOUNT THIS KIND ACTUALLY HAS. Sending the other one as null on
    // every save would quietly clear a field the user never saw — and the one
    // case where clearing IS right is a kind change, which is only possible on
    // an item that has not moved, so it is said explicitly.
    if (stocked) {
      body.cosAccountId = form.cosAccountId || null;
      if (editing && item.kind === 'non_inventory') body.expenseAccountId = null;
    } else {
      body.expenseAccountId = form.expenseAccountId || null;
      if (editing && item.kind !== 'non_inventory') body.cosAccountId = null;
    }

    // The two frozen fields are only ever sent when they are still editable.
    // Sending an unchanged `kind` on a moved item would be harmless (032
    // compares old to new) but sending it while the control is disabled is how
    // a form starts lying about what it submitted.
    if (!frozen) { body.kind = form.kind; body.code = form.code.trim(); }

    try {
      if (editing) await api.patch(`/api/entities/${entity.id}/stock-items/${item.id}`, body);
      else await api.post(`/api/entities/${entity.id}/stock-items`, body);
      onSaved(form.code.trim() || item.code);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const bothPriced = String(form.defaultSellPrice).trim() && String(form.markupPct).trim();

  return (
    <div style={{ margin: '16px 0', paddingTop: 12, borderTop: '1px solid var(--rule-strong)' }}>
      {err && <div className="error">{err}</div>}

      <div className="form-grid">
        <label className="field"><span>Kind</span>
          <select value={form.kind} disabled={frozen}
                  onChange={(e) => set({ kind: e.target.value })}>
            {Object.entries(KIND_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </label>
        <label className="field"><span>Code</span>
          <input className="input--figure" value={form.code} disabled={frozen}
                 onChange={(e) => set({ code: e.target.value })} /></label>
        <label className="field" style={{ minWidth: 240 }}><span>Name</span>
          <input value={form.name} onChange={(e) => set({ name: e.target.value })} /></label>
        <label className="field"><span>Unit</span>
          <input value={form.unit} placeholder="each"
                 onChange={(e) => set({ unit: e.target.value })} /></label>
      </div>

      {frozen && movedLoading && (
        <div className="note">
          Checking whether {item.code} has moved. Kind and code stay locked until that is
          known — unlocking them first and locking them a moment later would be worse than
          waiting.
        </div>
      )}
      {frozen && !movedLoading && (
        <div className="note note--warn">
          <strong>Kind and code are frozen.</strong> {item.code} already has stock layers or
          movements behind it. Changing the kind would strand serial numbers on rows whose kind no
          longer matches, and changing the code would rewrite every document that already names it.
          If it is genuinely the wrong kind, create a new item and transfer the stock across.
        </div>
      )}
      {editing && !frozen && movedUnknown && (
        <div className="note">
          Whether this item has moved could not be checked from here, so the kind and code are left
          editable. If it has, the server will refuse the change and say so.
        </div>
      )}

      <div className="form-grid">
        <label className="field" style={{ minWidth: 240 }}><span>Description</span>
          <input value={form.description} onChange={(e) => set({ description: e.target.value })} /></label>
        <label className="field"><span>Barcode</span>
          <input className="input--figure" value={form.barcode}
                 onChange={(e) => set({ barcode: e.target.value })} /></label>
      </div>

      <div className="form-grid">
        <label className="field" style={{ minWidth: 260 }}><span>Revenue account</span>
          <select value={form.revenueAccountId} onChange={(e) => set({ revenueAccountId: e.target.value })}>
            <option value="">Not set</option>
            {revenue.map((a) => <option key={a.id} value={a.id}>{a.code} {a.name}</option>)}
          </select>
        </label>

        {stocked ? (
          <label className="field" style={{ minWidth: 260 }}><span>Cost of sales account</span>
            <select value={form.cosAccountId} onChange={(e) => set({ cosAccountId: e.target.value })}>
              <option value="">Not set</option>
              {cos.map((a) => <option key={a.id} value={a.id}>{a.code} {a.name}</option>)}
            </select>
          </label>
        ) : (
          <label className="field" style={{ minWidth: 260 }}><span>Expense account</span>
            <select value={form.expenseAccountId} onChange={(e) => set({ expenseAccountId: e.target.value })}>
              <option value="">Not set</option>
              {expense.map((a) => <option key={a.id} value={a.id}>{a.code} {a.name}</option>)}
            </select>
          </label>
        )}
      </div>

      {stocked && !form.cosAccountId && (
        <div className="note">
          A stocked item must say where its cost of sales lands, or issuing it produces a cost
          figure with nothing behind it.
        </div>
      )}
      {!stocked && (
        <div className="note">
          Not stocked, so there is no cost of sales account — this item is never issued from a
          cost layer. Give it an expense account and a revenue account instead.
        </div>
      )}

      <div className="form-grid">
        <label className="field" style={{ minWidth: 240 }}><span>VAT on sale</span>
          <select value={form.salesTaxCodeId} onChange={(e) => set({ salesTaxCodeId: e.target.value })}>
            <option value="">Not set</option>
            {output.map((t) => <option key={t.id} value={t.id}>{t.code} {t.name}</option>)}
          </select>
        </label>
        <label className="field" style={{ minWidth: 240 }}><span>VAT on purchase</span>
          <select value={form.purchaseTaxCodeId} onChange={(e) => set({ purchaseTaxCodeId: e.target.value })}>
            <option value="">Not set</option>
            {input.map((t) => <option key={t.id} value={t.id}>{t.code} {t.name}</option>)}
          </select>
        </label>
      </div>

      <div className="form-grid">
        <label className="field"><span>Selling price</span>
          <input className="input--figure" value={form.defaultSellPrice}
                 disabled={Boolean(String(form.markupPct).trim())}
                 onChange={(e) => set({ defaultSellPrice: e.target.value })} /></label>
        <label className="field"><span>or Markup %</span>
          <input className="input--figure" value={form.markupPct}
                 disabled={Boolean(String(form.defaultSellPrice).trim())}
                 onChange={(e) => set({ markupPct: e.target.value })} /></label>
        <label className="field" style={{ justifyContent: 'flex-end' }}>
          <span>Active</span>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input type="checkbox" checked={form.isActive}
                   onChange={(e) => set({ isActive: e.target.checked })} />
            Available to capture against
          </label>
        </label>
      </div>

      {bothPriced && (
        <div className="note note--warn">
          An item has a selling price or a markup, never both — two answers to "what does this sell
          for" means the invoice uses whichever the code happened to check first. Clear one.
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
        <button className="btn" onClick={save}
                disabled={busy || !form.code.trim() || form.name.trim().length < 2
                  || (stocked && !form.cosAccountId) || Boolean(bothPriced)}>
          {busy ? 'Saving' : editing ? 'Save item' : 'Add item'}
        </button>
        <button className="btn btn--quiet" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}
